/**
 * A `<figured-bass>` read back into a stack of figures.
 *
 * A figure has no offset of its own. It stands where the reader stands, and a run of stacks
 * before one note walks forward by their own durations: the first covers as long as its
 * `<duration>` says, the second begins where the first left off, and so on until the note
 * is used up. So reading them needs a cursor of its own, carried alongside the bar's, and
 * that is `figureAhead` on the walk -- ticks from the note about to be written, reset every
 * time one is.
 *
 * A stack whose figures are all blank is the spacer a writer uses to hold the next stack
 * back to the beat it was written on. It carries no figure, so nothing is kept from it but
 * the distance it moves the cursor.
 */

import { markFor } from "../document/figures";
import type { Figure, FiguredBass } from "../document/figures";
import { createId } from "../document/ids";
import { attr, child, childrenNamed, numberOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import { fromDivisions } from "./common";

/** One `<figure>`, or `null` when it says nothing at all. */
function readFigure(element: XmlElement): Figure | null {
  const prefix = textOf(element, "prefix");
  const suffix = textOf(element, "suffix");
  const number = textOf(element, "figure-number");
  const digits = number === null ? null : Number.parseInt(number, 10);
  const extend = child(element, "extend");
  // `type="stop"` ends a line drawn from an earlier figure; it starts nothing here.
  const extending = extend !== null && attr(extend, "type") !== "stop";

  const figure: Figure = {
    prefix: prefix === null ? null : markFor(prefix),
    number: digits === null || Number.isNaN(digits) ? null : digits,
    suffix: suffix === null ? null : markFor(suffix),
    extend: extending,
  };
  const empty =
    figure.prefix === null && figure.number === null && figure.suffix === null && !extending;
  return empty ? null : figure;
}

/** What one `<figured-bass>` is worth: the stack, and how far it moves the cursor. */
export interface FiguredBassRead {
  /** The stack, or `null` when the element was only a spacer. */
  readonly stack: FiguredBass | null;
  /** Ticks to the next stack under the same note, from `<duration>`. */
  readonly ticks: number;
}

/** One `<figured-bass>` at the reader's cursor. */
export function readFiguredBass(
  element: XmlElement,
  divisions: number,
  onset: number,
): FiguredBassRead {
  const ticks = fromDivisions(numberOf(element, "duration", 0), divisions);
  const figures = childrenNamed(element, "figure")
    .map(readFigure)
    .filter((figure): figure is Figure => figure !== null);
  if (figures.length === 0) return { stack: null, ticks };

  return {
    stack: {
      id: createId("figure"),
      onset: Math.max(0, onset),
      figures,
      parenthesized: attr(element, "parentheses") === "yes",
    },
    ticks,
  };
}
